import { useState, useEffect } from "react";
import loginCardLogo from "../../assets/Logo_Full_Transparent.png";
import loginBackgroundWebp from "../../assets/Background-opt.webp";
import { Card, Input, Btn, IconEye, IconEyeOff } from "../../components/ui";
import { apiUrl, saveAuth, isNativeApp, setAuthToken, type AuthUser } from "../../utils/api";

export function LoginScreen({ onLogin }: { onLogin: (user: AuthUser) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"login" | "forgot">("login");
  const [info, setInfo] = useState<string | null>(null);
  const isDev = !!import.meta.env?.DEV;

  const requestReset = async () => {
    setError(null);
    setInfo(null);
    if (!email) {
      setError("Bitte E-Mail eingeben.");
      return;
    }
    setLoading(true);
    try {
      await fetch(apiUrl("/api/v1/auth/password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setInfo("Falls ein Konto mit dieser E-Mail existiert, wurde ein Link zum Zurücksetzen des Passworts versendet. Bitte prüfen Sie Ihr Postfach.");
    } catch {
      setInfo("Falls ein Konto mit dieser E-Mail existiert, wurde ein Link zum Zurücksetzen des Passworts versendet. Bitte prüfen Sie Ihr Postfach.");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (override?: { email: string; password: string }) => {
    setLoading(true);
    setError(null);
    try {
      const creds = override || { email, password };
      if (!creds.email || !creds.password) {
        throw new Error("Bitte E-Mail und Passwort eingeben.");
      }
      const resp = await fetch(apiUrl("/api/v1/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user: creds }),
      });
      if (!resp.ok) {
        const msg = await resp.json().catch(() => ({}));
        if (resp.status === 401) throw new Error("E-Mail oder Passwort ist falsch.");
        throw new Error((msg.errors || []).join(", ") || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      if (!data?.user) throw new Error("Ungueltige Antwort");
      // Native App: JWT speichern (Bearer-Auth statt Cookie). Im Web wird kein
      // Token gespeichert — dort bleibt das HttpOnly-Cookie die Auth-Quelle.
      if (isNativeApp()) setAuthToken(data.token);
      saveAuth(data.user);
      onLogin(data.user);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const quickLoginAdmin = () => {
    const localCreds = {
      email: import.meta.env.VITE_DEV_ADMIN_EMAIL as string || "admin@pflegefeen.local",
      password: import.meta.env.VITE_DEV_ADMIN_PASSWORD as string || "Admin!123",
    };
    setEmail(localCreds.email);
    setPassword(localCreds.password);
    submit(localCreds);
  };

  useEffect(() => {
    document.body.style.setProperty("--login-bg", `url(${loginBackgroundWebp})`);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.removeProperty("--login-bg");
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackgroundWebp})` }}
    >
      <Card className="p-6 w-full max-w-md grid gap-4">
        <img
          src={loginCardLogo}
          alt="Logo"
          className="w-full h-auto object-contain"
        />
        <div className="text-xl font-bold" style={{ fontFamily: '"Cinzel", serif' }}>
          {mode === "forgot" ? "Passwort vergessen" : "Anmelden"}
        </div>
        <Input type="email" placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} />

        {mode === "forgot" ? (
          <>
            <div className="text-sm text-slate-600">
              Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link, um ein neues Passwort zu setzen.
            </div>
            {info && <div className="text-sm text-[var(--green-dark)]">{info}</div>}
            {error && <div className="text-sm text-[var(--red)]">{error}</div>}
            <Btn onClick={requestReset} disabled={loading}>{loading ? "..." : "Link senden"}</Btn>
            <button
              type="button"
              onClick={() => { setMode("login"); setError(null); setInfo(null); }}
              className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
            >
              Zurück zur Anmeldung
            </button>
          </>
        ) : (
          <>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Passwort"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                title={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
              >
                {showPassword ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            {error && <div className="text-sm text-[var(--red)]">{error}</div>}
            <Btn onClick={() => submit()} disabled={loading}>{loading ? "..." : "Login"}</Btn>
            <button
              type="button"
              onClick={() => { setMode("forgot"); setError(null); setInfo(null); }}
              className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2 text-center"
            >
              Passwort vergessen?
            </button>
            <div className="text-center text-sm text-slate-600">
              Noch kein Kunde?{" "}
              <a
                href="https://sonne-qm.de"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[var(--green-dark)] underline underline-offset-2 hover:text-[var(--green-dark)]"
              >
                Hier kostenlos testen
              </a>
            </div>
            {isDev && (
              <Btn variant="secondary" onClick={quickLoginAdmin} disabled={loading}>
                Lokal: als System-Admin einloggen
              </Btn>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
